import { VentaDiaria } from '../inicio.service';
import { SemanaData, DiaSemana } from './types';

// Logica derivada de ventas diarias, en base a un rango [desde, hasta] elegido por el usuario.
// La consulta a DB trae el DOBLE del rango hacia atras para poder comparar cada periodo
// contra el periodo anterior de igual longitud.

const MS_DIA = 86400000;

function addDays(base: Date, dias: number): Date {
  const d = new Date(base);
  d.setDate(d.getDate() + dias);
  return d;
}

// Lunes (00:00) de la semana que contiene `fecha`.
function lunesDe(fecha: Date): Date {
  const d = new Date(fecha);
  d.setHours(0, 0, 0, 0);
  const dow = (d.getDay() + 6) % 7; // 0 = Lunes ... 6 = Domingo
  d.setDate(d.getDate() - dow);
  return d;
}

// Cantidad de dias del rango (inclusivo).
export function rangoDias(desde: Date, hasta: Date): number {
  return Math.max(1, Math.round((hasta.getTime() - desde.getTime()) / MS_DIA));
}

// Cantidad de semanas que abarca el rango.
export function numSemanas(desde: Date, hasta: Date): number {
  return Math.max(1, Math.round(rangoDias(desde, hasta) / 7));
}

function sumRango(ventas: VentaDiaria[], d1: Date, d2: Date): { pesos: number; kg: number } {
  return ventas.reduce(
    (acc, v) => {
      const d = new Date(v.fecha);
      if (d >= d1 && d <= d2) {
        acc.pesos += v.totalPesos || 0;
        acc.kg += v.totalKg || 0;
      }
      return acc;
    },
    { pesos: 0, kg: 0 }
  );
}

// Una barra por semana CALENDARIO (lunes -> domingo) que toca el rango,
// comparada contra la semana calendario inmediata anterior.
// `ventasAnterior` (periodo previo) cubre la semana anterior a la primera.
export function calcularSemanas(
  ventas: VentaDiaria[],
  ventasAnterior: VentaDiaria[],
  desde: Date,
  hasta: Date
): SemanaData[] {
  const todas = [...ventas, ...ventasAnterior];
  const primerLunes = lunesDe(desde);
  const result: SemanaData[] = [];
  // De la semana mas reciente a la mas antigua.
  for (let wDesde = lunesDe(hasta); wDesde >= primerLunes; wDesde = addDays(wDesde, -7)) {
    const wHasta = addDays(wDesde, 6);
    const finSemana = new Date(wHasta); finSemana.setHours(23, 59, 59, 999);
    const finPrev = addDays(wDesde, -1); finPrev.setHours(23, 59, 59, 999);
    const cur = sumRango(todas, wDesde, finSemana);
    const prev = sumRango(todas, addDays(wDesde, -7), finPrev);
    result.push({
      label: `${wDesde.getDate()}/${wDesde.getMonth() + 1} – ${wHasta.getDate()}/${wHasta.getMonth() + 1}`,
      totalPesos: cur.pesos,
      totalKg: cur.kg,
      pct: prev.pesos > 0 ? Math.round((cur.pesos / prev.pesos - 1) * 100) : 0,
      pctKg: prev.kg > 0 ? Math.round((cur.kg / prev.kg - 1) * 100) : 0
    });
  }
  return result;
}

export function semanaActual(ventas: VentaDiaria[], ventasAnterior: VentaDiaria[], desde: Date, hasta: Date): SemanaData {
  return calcularSemanas(ventas, ventasAnterior, desde, hasta)[0] ?? { label: '', totalPesos: 0, totalKg: 0, pct: 0, pctKg: 0 };
}

export function totalPesosPeriodo(ventas: VentaDiaria[], desde: Date, hasta: Date): number {
  return sumRango(ventas, desde, hasta).pesos;
}

export function totalKgPeriodo(ventas: VentaDiaria[], desde: Date, hasta: Date): number {
  return sumRango(ventas, desde, hasta).kg;
}

export function promCrecimiento(
  ventas: VentaDiaria[],
  ventasAnterior: VentaDiaria[],
  campo: 'pct' | 'pctKg',
  desde: Date,
  hasta: Date
): number {
  const crecs = calcularSemanas(ventas, ventasAnterior, desde, hasta).map(w => w[campo]).filter(p => p !== 0);
  return crecs.length ? Math.round(crecs.reduce((a, b) => a + b, 0) / crecs.length) : 0;
}

// Desglose de ingresos por dia de la semana: acumula el rango agrupando por dia.
// Color verde si el dia rinde >= el mismo dia del periodo anterior, rojo si esta por debajo.
export function calcularDiasSemana(ventas: VentaDiaria[], ventasAnterior: VentaDiaria[]): DiaSemana[] {
  // Orden de negocio: Lunes -> Domingo. getDay(): 0=Dom..6=Sab.
  const orden = [1, 2, 3, 4, 5, 6, 0];
  const dowNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const agrupar = (data: VentaDiaria[]) => {
    const acum = new Map<number, { pesos: number; kg: number }>();
    orden.forEach(dow => acum.set(dow, { pesos: 0, kg: 0 }));
    for (const v of data) {
      const b = acum.get(new Date(v.fecha).getDay())!;
      b.pesos += v.totalPesos || 0;
      b.kg += v.totalKg || 0;
    }
    return acum;
  };

  const actual = agrupar(ventas);
  const previo = agrupar(ventasAnterior);

  return orden.map(dow => {
    const b = actual.get(dow)!;
    const p = previo.get(dow)!;
    return {
      nombre: dowNames[dow],
      pesos: b.pesos,
      kg: b.kg,
      color: p.pesos === 0 || b.pesos >= p.pesos ? '#22c55e' : '#f87171',
      colorKg: p.kg === 0 || b.kg >= p.kg ? '#22c55e' : '#f87171'
    };
  });
}
